import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, ArrowLeft } from "lucide-react";

const CreateEventPage = () => {
  const [data, setData] = useState({
    name: "", type: "wedding", date: "", description: "", cover_image: "",
    moderation: false, allow_photos: true, allow_videos: true, allow_messages: true,
    allow_rsvp: false, theme_color: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const update = (k, v) => setData({ ...data, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const res = await api.post("/events", data);
      nav(`/dashboard/events/${res.data.id}`);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create event");
    } finally { setBusy(false); }
  };

  const types = ["wedding", "party", "business", "memorial", "engagement", "kids-party"];

  return (
    <section className="min-h-[85vh] bg-cream-100 py-12">
      <div className="max-w-2xl mx-auto px-5">
        <button onClick={() => nav(-1)} className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
        <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Create Event</p>
        <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">New <span className="italic">private gallery</span></h1>

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl soft-shadow p-8 space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Event type</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {types.map(t => (
                <button key={t} type="button" onClick={() => update("type", t)} className={`py-2.5 rounded-lg border-2 text-sm font-semibold capitalize transition-colors ${data.type === t ? "border-coral-300 bg-coral-100/40 text-coral-500" : "border-cream-200 text-navy-700 hover:border-coral-200"}`}>{t.replace("-", " ")}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Event name</label>
            <input required value={data.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Madison & Aiden" className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Date</label>
              <input type="date" value={data.date} onChange={(e) => update("date", e.target.value)} className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Cover image URL</label>
              <input value={data.cover_image} onChange={(e) => update("cover_image", e.target.value)} placeholder="https://..." className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Welcome message (optional)</label>
            <textarea rows="3" value={data.description} onChange={(e) => update("description", e.target.value)} placeholder="Welcome guests with a short note..." className="mt-2 w-full px-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300" />
          </div>
          <div className="space-y-3 pt-2">
            {[
              ["allow_photos", "Allow photo uploads"],
              ["allow_videos", "Allow video uploads"],
              ["allow_messages", "Allow guestbook messages"],
              ["moderation", "Require host approval before showing uploads"],
              ["allow_rsvp", "Collect RSVPs from guests"]
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 text-sm text-navy-700 cursor-pointer">
                <input type="checkbox" checked={data[k]} onChange={(e) => update(k, e.target.checked)} data-testid={`create-event-toggle-${k}`} className="w-4 h-4 accent-coral-300" />
                {label}
              </label>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Theme accent color <span className="text-navy-700/40 normal-case font-normal">(optional — Classic & Grand)</span></label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="color"
                value={data.theme_color || "#e76f51"}
                onChange={(e) => update("theme_color", e.target.value)}
                data-testid="create-event-theme-color"
                className="w-12 h-11 rounded-lg border border-cream-200 cursor-pointer"
                aria-label="Pick theme accent color"
              />
              <input
                type="text"
                value={data.theme_color}
                onChange={(e) => update("theme_color", e.target.value)}
                placeholder="#e76f51"
                maxLength={9}
                className="flex-1 px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm font-mono"
              />
              {data.theme_color && (
                <button type="button" onClick={() => update("theme_color", "")} className="text-xs text-navy-700/50 hover:text-coral-400 underline">clear</button>
              )}
            </div>
            <p className="text-xs text-navy-700/50 mt-1">Applied to buttons and highlights on your guest gallery.</p>
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
          <button disabled={busy} type="submit" className="w-full bg-coral-300 text-white py-3 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? "Creating..." : "Create gallery"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
};

export default CreateEventPage;
